import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client';
import { Mail, User } from 'lucide-react';

import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextSeparator,
  useErrorHandler,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  CountryPicker,
  parseCalendarDate,
  StatePicker,
  toCalendarDate,
} from '@sandtech-hub-monorepo/ui-kit-site';
import {
  useCognito,
  useGetMaybeViewer,
} from '@sandtech-hub-monorepo/ui-auth-kit';
import {
  Gender,
  UpdateUserConsentDocument,
  UpdateUserDocument,
  ViewerDocument,
} from '@sandtech-hub-monorepo/ehub-client';

import { ReactComponent as CheckIcon } from '../../assets/Icons/check-icon.svg';
import { AppRoutesPaths } from '../../routes/types';

const genderLabels: Record<Gender, string> = {
  [Gender.Female]: 'Female',
  [Gender.Male]: 'Male',
  [Gender.NonBinary]: 'Non-binary',
  [Gender.Other]: 'Others',
  [Gender.PreferNotToSay]: 'Prefer not to say',
};

const currentYear = new Date().getFullYear();

const formSchema = z.object({
  // Deliberately not pre-filled from `viewer` (see the reset below) - at
  // signup FIT collects no name at all, so `viewer.firstName`/`lastName`
  // hold a throwaway placeholder (an email-derived string / "Pending",
  // sent only because Cognito's given_name/family_name are `required`).
  // This is the first real chance to collect the learner's actual name.
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  gender: z.nativeEnum(Gender, {
    required_error: 'Gender is required',
  }),
  birthDate: z.date({ required_error: 'Date of birth is required' }),
  countryOfResidence: z.string().optional(),
  countyOfResidence: z.string().optional(),
  marketingConsent: z.boolean().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export const Onboarding = () => {
  const navigate = useNavigate();
  const { viewer } = useGetMaybeViewer();
  const { signOut } = useCognito();
  const { handleGeneralAppError } = useErrorHandler();

  const [updateUserConsent] = useMutation(UpdateUserConsentDocument, {
    onError: (error) => handleGeneralAppError(error.message),
  });

  /*
   * The viewer is refetched, and awaited, before navigating. This page exists
   * only because `GuardedRoutes` read `viewer.gender` / `viewer.birthDate` and
   * found them empty, and that same read decides whether leaving is allowed —
   * so navigating on the mutation's own result raced the state the guard
   * consults, and a learner who had just completed onboarding was sent
   * straight back to it. Awaiting the refetch means the guard is reading the
   * server's answer, not ours, by the time it runs.
   *
   * Consent is deliberately not awaited: it writes a different type, no gate
   * reads it, and nobody should wait on a marketing flag to enter the app.
   */
  const [updateUser, { loading }] = useMutation(UpdateUserDocument, {
    onError: (error) => handleGeneralAppError(error.message),
    refetchQueries: [ViewerDocument],
    awaitRefetchQueries: true,
    onCompleted: () => {
      toast.success('Profile completed. Welcome to Frontier!');
      navigate(AppRoutesPaths.Home);
    },
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (viewer) {
      form.reset({
        firstName: '',
        lastName: '',
        gender: (viewer.gender as Gender) || undefined,
        birthDate: parseCalendarDate(viewer.birthDate) ?? undefined,
        countryOfResidence: viewer.countryOfResidence || undefined,
        countyOfResidence: viewer.countyOfResidence || undefined,
        marketingConsent: false,
      });
    }
  }, [viewer, form]);

  const selectedCountry = form.watch('countryOfResidence');

  const handleSubmit = (values: FormValues) => {
    updateUser({
      variables: {
        input: {
          firstName: values.firstName,
          lastName: values.lastName,
          gender: values.gender,
          // `birthDate` is the GraphQL `Date` scalar: a calendar day, sent as
          // YYYY-MM-DD. toISOString() would send an instant, which the scalar
          // rejects outright, and whose UTC day is not the day that was picked.
          birthDate: toCalendarDate(values.birthDate),
          // Send these only when set: picking a country but no state leaves
          // countyOfResidence as '' (see the CountryPicker reset above), and
          // an empty string can be rejected by the region-validating backend.
          countryOfResidence: values.countryOfResidence || undefined,
          countyOfResidence: values.countyOfResidence || undefined,
        },
      },
    });

    if (values.marketingConsent) {
      updateUserConsent({
        variables: {
          input: { marketingConsentAt: new Date().toISOString() },
        },
      });
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-3xl bg-card rounded-3xl border border-border px-16 pt-12 pb-16 flex flex-col gap-6">
        <div className="flex flex-col items-center text-center gap-2">
          <div className="p-3 border border-border rounded-xl shadow-sm text-foreground">
            <CheckIcon className="h-7 w-7"/>
          </div>
          <h1 className="text-3xl font-semibold text-primary">
            Complete Your Profile
          </h1>
          <TextSeparator
            text="We just need a few more details to get you started."
            textClassName="text-base text-center !bg-card"
          />
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-normal">
                      First Name (Legal) *
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter your first name"
                        endAdornment={<User className="h-5 w-5" />}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-normal">
                      Last Name (Legal) *
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter your last name"
                        endAdornment={<User className="h-5 w-5" />}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormItem>
              <FormLabel className="font-normal">Primary Email</FormLabel>
              <FormControl>
                <Input
                  value={viewer?.primaryEmail || ''}
                  disabled
                  readOnly
                  endAdornment={<Mail className="h-5 w-5" />}
                />
              </FormControl>
            </FormItem>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="font-normal">Gender *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your gender" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(Gender).map((value) => (
                          <SelectItem key={value} value={value}>
                            {genderLabels[value]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="birthDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="font-normal">
                      Date of Birth *
                    </FormLabel>
                    <FormControl>
                      <DatePicker
                        selected={field.value}
                        onSelect={field.onChange}
                        placeholder="DD / MM / YYYY"
                        fromYear={1940}
                        toYear={currentYear}
                        disabledDates={(date) => date > new Date()}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="countryOfResidence"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-normal">Country</FormLabel>
                    <FormControl>
                      <CountryPicker
                        selected={field.value || undefined}
                        handleOnSelect={({ value }) => {
                          if (value !== field.value) {
                            form.setValue('countyOfResidence', '');
                          }
                          field.onChange(value);
                        }}
                        triggerClassName="rounded-md"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="countyOfResidence"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-normal">State</FormLabel>
                    <FormControl>
                      <StatePicker
                        countryCode={selectedCountry || undefined}
                        selected={field.value || undefined}
                        handleOnSelect={({ value }) => field.onChange(value)}
                        triggerClassName="rounded-md"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/*
              Tacit approval, not an explicit opt-in: completing the form is
              the acceptance, so this is plain text next to the submit
              button rather than a gating checkbox/FormField.
            */}
            <p className="text-xs font-normal text-foreground">
              By signing up I accept Frontier&apos;s{' '}
              <Link
                to="https://www.frontiertech.edu/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary"
              >
                terms of services
              </Link>{' '}
              and acknowledge the{' '}
              <Link
                to="https://frontiertech.edu/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary"
              >
                privacy policy
              </Link>
              .
            </p>

            <FormField
              control={form.control}
              name="marketingConsent"
              render={({ field }) => (
                <FormItem className="flex space-y-0 gap-2 items-start">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="text-xs font-normal leading-4 text-foreground">
                    I agree to receive commercial communications from Frontier
                    Institute of Technology through all available channels and
                    may opt out at any time.
                  </FormLabel>
                </FormItem>
              )}
            />

            {/*
              `loading` spans the refetch as well as the mutation: with
              `awaitRefetchQueries` the mutation promise resolves only once the
              viewer has come back, so this covers the whole wait rather than
              just the write. Two round trips is long enough that a greyed-out
              button with unchanged text reads as nothing happening.
            */}
            <Button
              type="submit"
              className="w-full h-11"
              disabled={loading || !form.formState.isValid}
            >
              {loading ? 'Completing…' : 'Complete Sign Up'}
            </Button>

            {/*
              Signs out rather than linking to /login. The learner is already
              authenticated here — onboarding is behind the guard — so the login
              page would either bounce them straight back or leave them looking
              at a sign-in form for the session they are still in.
              No navigation needed: clearing the session makes the guard send
              them to /login, and the Apollo cache is wiped by the `signedOut`
              Hub listener so nothing of theirs outlives the session.
            */}
            <button
              type="button"
              onClick={signOut}
              className="text-sm text-muted-foreground font-semibold text-center"
            >
              ← Back to log in
            </button>
          </form>
        </Form>
      </div>
    </div>
  );
};
